import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { shortId } from '@/ops/format';
import { REPLICA } from '@/ops/live';

/**
 * The tenants of one environment from the console's saved copy — the name source for pickers and labels. Shares its
 * cache with the Tenants list, so a page that shows names costs no extra request once the list was opened.
 */
export function useTenantDirectory(env: string | null | undefined) {
  return useQuery({
    queryKey: ['ops', 'env-tenants', env],
    queryFn: () => opsApi.tenants.list(env!),
    enabled: !!env && hasPermission(PERMISSIONS.hostsRead),
    ...REPLICA,
    staleTime: 60_000,
  });
}

/** A tenant's name (linking to its page) instead of its id; the short id when the saved copy does not know it. */
export function TenantName({ env, id, link = true, tab }: { env: string | null | undefined; id: string; link?: boolean; tab?: string }) {
  const directory = useTenantDirectory(env);
  const name = directory.data?.tenants.find((t) => t.tenant_id === id)?.name;
  const text = name ?? shortId(id);
  const body = <span className={name ? undefined : 'mono t-caption'} title={id}>{text}</span>;
  if (!link || !env) return body;
  return <Link to={`/ops/tenants/${encodeURIComponent(env)}/${id}${tab ? `?tab=${tab}` : ''}`}>{body}</Link>;
}

/**
 * Choose a tenant by name (searchable) instead of typing its id. The empty value means "all tenants" when
 * `placeholder` says so.
 */
export function TenantPicker({
  env, value, onChange, label = 'Tenant', placeholder = 'All tenants', inline = false,
}: {
  env: string | null | undefined;
  value: string;
  onChange: (tenantId: string) => void;
  label?: string;
  placeholder?: string;
  inline?: boolean;
}) {
  const directory = useTenantDirectory(env);
  const tenants = directory.data?.tenants ?? [];
  const options = tenants
    .map((t) => ({ value: t.tenant_id, label: t.name || '(unnamed)', hint: shortId(t.tenant_id) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
  // A value the saved copy does not know (a link from elsewhere) stays selectable rather than silently dropped.
  if (value && !options.some((o) => o.value === value)) options.unshift({ value, label: shortId(value), hint: 'not in the saved copy' });

  return (
    <Select
      label={label}
      inline={inline}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      options={options}
      searchable
      searchPlaceholder="Type a tenant name…"
      hint={directory.isError ? 'The tenant list could not be loaded.' : undefined}
    />
  );
}

/** Several tenants by name: a picker to add one, a chip per chosen tenant to remove it. */
export function TenantMultiPicker({
  env, value, onChange, label = 'Tenants',
}: {
  env: string | null | undefined;
  value: string[];
  onChange: (tenantIds: string[]) => void;
  label?: string;
}) {
  return (
    <div className="stack-sm">
      <TenantPicker env={env} value="" label={label} placeholder="Add a tenant…"
        onChange={(id) => { if (id && !value.includes(id)) onChange([...value, id]); }} />
      {value.length > 0 && (
        <div className="row">
          {value.map((id) => (
            <span key={id} className="chip chip-info">
              <TenantName env={env} id={id} link={false} />
              <button type="button" className="chip-remove" aria-label="Remove" onClick={() => onChange(value.filter((v) => v !== id))}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
